/*
 * twe-brand-groups.js
 * -------------------
 * Keeps the collection grid grouped by product vendor (brand) even after the
 * custom TWE filter has replaced the grid with a flat list of cards.
 *
 * How the pieces fit together:
 *
 *   snippets/collection-template-brands.liquid renders
 *     .collection-grid__wrapper
 *       > .grid.grid--uniform[data-twe-brand-landing]   <-- empty on load
 *       > [data-twe-brand-groups]
 *           > section.twe-brand-group  (heading + its own .grid.grid--uniform)
 *
 *   snippets/twe-product-filters.liquid resolves its render target as
 *   `wrapper.querySelector('.grid--uniform')`, which is the landing grid.
 *   So filtered cards arrive in the landing grid; this script moves them into
 *   freshly built brand groups and leaves the landing node itself in place
 *   (the filter caches that node, so it must not be detached).
 *
 *   Clearing all filters restores the wrapper's original HTML, which is the
 *   server-rendered grouped markup — this script then has nothing to do.
 */
(function () {
  'use strict';

  var WRAPPER_SEL = '.collection-grid__wrapper';
  var LANDING_SEL = '[data-twe-brand-landing]';
  var GROUPS_SEL = '[data-twe-brand-groups]';
  var CARD_SEL = '[data-product-handle]';

  var state = {
    vendors: Object.create(null), // handle -> vendor
    prices: Object.create(null), // handle -> current price in cents
    order: [],
    fallback: 'Other',
    price: '' // active price range, "min-max" in euros ('' = all)
  };

  var PRICE_PARAM = 'price';

  var observer = null;
  var applying = false;
  var queued = false;
  var filterDataMerged = false;

  // ── helpers ──────────────────────────────────────────────────────────────

  function handleize(str) {
    return String(str)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'brand';
  }

  function readBrandMap() {
    var el = document.querySelector('[data-twe-brand-map]');
    if (!el) return false;
    try {
      var data = JSON.parse(el.textContent);
      if (data.vendors) {
        Object.keys(data.vendors).forEach(function (h) {
          state.vendors[h] = data.vendors[h];
        });
      }
      if (data.prices) {
        Object.keys(data.prices).forEach(function (h) {
          state.prices[h] = data.prices[h];
        });
      }
      if (Array.isArray(data.order)) state.order = data.order.slice();
      if (data.fallback) state.fallback = data.fallback;
      return true;
    } catch (e) {
      return false;
    }
  }

  /*
   * The filter feed already carries a vendor for every product in the shop, so
   * borrow it. This covers products that live on another pagination page and
   * are therefore missing from the server-rendered map.
   */
  function mergeFilterData() {
    if (filterDataMerged || !window.__tweFilterDataPromise) return;
    filterDataMerged = true;
    window.__tweFilterDataPromise
      .then(function (data) {
        if (!data || !data.products) return;
        var added = false;
        data.products.forEach(function (p) {
          if (p && p.handle && !state.vendors[p.handle]) {
            state.vendors[p.handle] = p.vendor || state.fallback;
            added = true;
          }
        });
        if (added) schedule();
      })
      .catch(function () {});
  }

  function vendorOfCard(card) {
    var handle = card.getAttribute('data-product-handle');
    if (handle && state.vendors[handle]) return state.vendors[handle];

    // Card markup exposes the vendor when settings.vendor_enable is on.
    var printed = card.querySelector('.grid-product__vendor');
    if (printed && printed.textContent.trim()) return printed.textContent.trim();

    mergeFilterData();
    return state.fallback;
  }

  function isCard(node) {
    return node.nodeType === 1 && node.matches && node.matches(CARD_SEL);
  }

  // ── rendering ────────────────────────────────────────────────────────────

  function buildGroup(vendor, cards, opts) {
    var section = document.createElement('section');
    section.className = 'twe-brand-group';
    section.id = 'brand-' + handleize(vendor);
    section.setAttribute('data-twe-brand-group', '');
    section.setAttribute('data-brand', vendor);

    var head = document.createElement('div');
    head.className = 'twe-brand-group__head' + (opts.reverse ? ' twe-brand-group__head--reverse' : '');

    var title = document.createElement('h2');
    title.className = 'twe-brand-group__title';
    title.textContent = vendor;
    head.appendChild(title);

    if (opts.showCount) {
      var count = document.createElement('span');
      count.className = 'twe-brand-group__count';
      count.textContent = String(cards.length);
      head.appendChild(count);
    }

    // The brush-stroke rule is a CSS background, so there is nothing to clone.
    var line = document.createElement('span');
    line.className = 'twe-brand-group__line';
    line.setAttribute('aria-hidden', 'true');
    head.appendChild(line);

    var grid = document.createElement('div');
    grid.className = 'grid grid--uniform twe-brand-group__grid' + (opts.flush ? ' small--grid--flush' : '');
    cards.forEach(function (card) { grid.appendChild(card); });

    section.appendChild(head);
    section.appendChild(grid);
    return section;
  }

  function syncBrandNav(present) {
    var nav = document.querySelector('[data-twe-brand-nav]');
    if (!nav) return;
    var keys = present.map(function (v) { return String(v).toLowerCase(); });
    var any = false;
    nav.querySelectorAll('[data-brand-link]').forEach(function (chip) {
      var on = keys.indexOf(chip.getAttribute('data-brand-link').toLowerCase()) !== -1;
      chip.hidden = !on;
      if (on) any = true;
    });
    nav.hidden = !any;
  }

  function nudgeLazyImages(root) {
    if (!window.lazySizes || !window.lazySizes.loader) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.querySelectorAll('.lazyload, .lazyloading').forEach(function (el) {
          try { window.lazySizes.loader.unveil(el); } catch (e) {}
        });
      });
    });
  }

  // ── the main pass ────────────────────────────────────────────────────────

  function apply() {
    var wrapper = document.querySelector(WRAPPER_SEL);
    if (!wrapper) return;

    var landing = wrapper.querySelector(LANDING_SEL);
    var groups = wrapper.querySelector(GROUPS_SEL);
    if (!landing || !groups) return;

    var children = Array.prototype.slice.call(landing.children);
    var cards = children.filter(isCard);

    if (!cards.length) {
      if (children.length) {
        // The filter wrote a "no products match" message into the landing grid.
        groups.innerHTML = '';
        landing.classList.add('twe-brand-landing--message');
        syncBrandNav([]);
      }
      return;
    }

    landing.classList.remove('twe-brand-landing--message');

    var showCount = groups.getAttribute('data-show-count') === 'true';
    var alternate = groups.getAttribute('data-alternate') !== 'false';
    var flush = groups.classList.contains('twe-brand-groups--flush');

    /*
     * Bucket the cards, preserving the filter's own sort order within a brand.
     * Keys are lower-cased so a stray "IAMElectric" does not split off from
     * "IAMelectric"; the display spelling comes from the pinned/server order
     * when there is one, otherwise from the first card seen.
     */
    var byKey = Object.create(null);
    var labelOf = Object.create(null);
    var appearance = [];

    state.order.forEach(function (v) {
      labelOf[v.toLowerCase()] = v;
    });

    cards.forEach(function (card) {
      var vendor = vendorOfCard(card);
      var key = String(vendor).toLowerCase();
      if (!byKey[key]) {
        byKey[key] = [];
        appearance.push(key);
        if (!labelOf[key]) labelOf[key] = vendor;
      }
      byKey[key].push(card);
    });

    // Pinned/server order first, then anything new in order of appearance.
    var ordered = state.order
      .map(function (v) { return v.toLowerCase(); })
      .filter(function (k) { return byKey[k]; });
    appearance.forEach(function (k) {
      if (ordered.indexOf(k) === -1) ordered.push(k);
    });

    var frag = document.createDocumentFragment();
    var presentLabels = ordered.map(function (k) { return labelOf[k]; });
    ordered.forEach(function (key, i) {
      frag.appendChild(buildGroup(labelOf[key], byKey[key], {
        showCount: showCount,
        flush: flush,
        reverse: alternate && i % 2 === 1
      }));
    });

    applying = true;
    if (observer) observer.disconnect();
    try {
      groups.innerHTML = '';
      groups.appendChild(frag); // moving the cards also empties the landing grid
    } finally {
      applying = false;
      connect();
    }

    syncBrandNav(presentLabels);
    applyPrice();
    nudgeLazyImages(groups);

    if (window.AOS && typeof window.AOS.refreshHard === 'function') {
      try { window.AOS.refreshHard(); } catch (e) {}
    }
    if (window.StampedFn) {
      try { window.StampedFn.loadBadges(); } catch (e) {}
    }
  }

  /*
   * setTimeout, not requestAnimationFrame: rAF is throttled to zero in a
   * background tab, so a shopper who filters and switches tabs would come back
   * to an ungrouped grid that never repairs itself.
   */
  function schedule() {
    if (applying || queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      apply();
    }, 0);
  }

  function connect() {
    var wrapper = document.querySelector(WRAPPER_SEL);
    if (!wrapper) return;
    if (!observer) {
      observer = new MutationObserver(function () {
        if (applying) return;
        schedule();
      });
    }
    observer.observe(wrapper, { childList: true, subtree: true });
  }

  // ── price-range chips ────────────────────────────────────────────────────

  /*
   * Ranges are "min-max" in whole euros, lower bound exclusive and upper bound
   * inclusive; "7500-" is open-ended. The server already rendered chips with
   * nothing in the current set as [hidden], so only real ranges are offered.
   * The choice lives in ?price= so it survives the Use / Rider tag links.
   */
  function parseRange(str) {
    var parts = String(str || '').split('-');
    var min = parseFloat(parts[0]);
    if (isNaN(min)) return null;
    var max = parts.length > 1 && parts[1] !== '' ? parseFloat(parts[1]) : Infinity;
    return { min: min * 100, max: isNaN(max) ? Infinity : max * 100 };
  }

  function inRange(price, range) {
    return price > range.min && price <= range.max;
  }

  function syncPriceUrl() {
    var url;
    try { url = new URL(window.location.href); } catch (e) { return; }
    if (state.price) url.searchParams.set(PRICE_PARAM, state.price);
    else url.searchParams.delete(PRICE_PARAM);
    try { history.replaceState(history.state, '', url.toString()); } catch (e) {}

    // Carry the range across the server-side tag filters.
    document.querySelectorAll('[data-twe-tag-chip]').forEach(function (a) {
      var u;
      try { u = new URL(a.getAttribute('href'), window.location.origin); } catch (e) { return; }
      if (state.price) u.searchParams.set(PRICE_PARAM, state.price);
      else u.searchParams.delete(PRICE_PARAM);
      a.setAttribute('href', u.pathname + u.search);
    });
  }

  function applyPrice() {
    var group = document.querySelector('[data-twe-price-group]');
    var groups = document.querySelector(GROUPS_SEL);
    if (!group || !groups) return;

    var chips = Array.prototype.slice.call(group.querySelectorAll('[data-twe-price]'));
    var range = parseRange(state.price);

    // A range carried over from another page may be empty here: fall back to all.
    var activeChip = null;
    chips.forEach(function (chip) {
      if (chip.getAttribute('data-twe-price') === state.price && !chip.hidden) activeChip = chip;
    });
    if (!activeChip) {
      state.price = '';
      range = null;
    }

    chips.forEach(function (chip) {
      var on = chip.getAttribute('data-twe-price') === state.price;
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var present = [];
    groups.querySelectorAll('[data-twe-brand-group]').forEach(function (section) {
      var visible = 0;
      section.querySelectorAll(CARD_SEL).forEach(function (card) {
        var price = state.prices[card.getAttribute('data-product-handle')];
        var show = !range || (typeof price === 'number' && inRange(price, range));
        card.hidden = !show;
        if (show) visible++;
      });
      section.hidden = visible === 0;
      if (visible) present.push(section.getAttribute('data-brand'));
    });

    syncBrandNav(present);
    syncPriceUrl();
  }

  function bindPrice() {
    var group = document.querySelector('[data-twe-price-group]');
    if (!group) return;

    var fromUrl = '';
    try { fromUrl = new URL(window.location.href).searchParams.get(PRICE_PARAM) || ''; } catch (e) {}
    state.price = fromUrl;

    group.addEventListener('click', function (e) {
      var chip = e.target.closest && e.target.closest('[data-twe-price]');
      if (!chip) return;
      state.price = chip.getAttribute('data-twe-price');
      applyPrice();
      nudgeLazyImages(document.querySelector(GROUPS_SEL) || document);
      if (window.AOS && typeof window.AOS.refreshHard === 'function') {
        try { window.AOS.refreshHard(); } catch (err) {}
      }
    });
  }

  // ── sticky brand bar: offset + jump links ────────────────────────────────

  /*
   * The brand bar is sticky on mobile, pinned below the theme's own header
   * (.site-header--stuck goes position:fixed;top:0 past 250px of scroll).
   * Publish the header's height as --tweBrandNavTop so the CSS pin and the
   * jump-link offset both track it instead of hardcoding a number.
   */
  function syncNavOffset() {
    var root = document.documentElement;

    // clientWidth excludes the scrollbar; 100vw does not.
    root.style.setProperty('--tweVw', root.clientWidth + 'px');

    var header = document.querySelector('.site-header');
    if (!header) return;
    var h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) {
      root.style.setProperty('--tweBrandNavTop', h + 'px');
    }
  }

  /*
   * The header's height changes when it pins (.site-header--stuck adds 10px of
   * vertical padding above 769px), so re-measure when that class lands rather
   * than trusting the at-rest height.
   */
  function watchHeader() {
    var header = document.querySelector('.site-header');
    if (!header || typeof MutationObserver !== 'function') return;
    new MutationObserver(syncNavOffset).observe(header, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /*
   * How far above a brand heading to stop, so the heading clears everything
   * pinned over it. On mobile that is the header plus the brand bar itself;
   * on desktop the bar scrolls away, so only the header counts.
   */
  function jumpOffset() {
    var GAP = 16;
    var nav = document.querySelector('[data-twe-brand-nav]');
    var header = document.querySelector('.site-header');
    var headerH = header ? header.getBoundingClientRect().height : 0;

    if (!nav || getComputedStyle(nav).position !== 'sticky') {
      return headerH + GAP;
    }
    var pinnedAt = parseFloat(getComputedStyle(nav).top);
    if (isNaN(pinnedAt)) pinnedAt = headerH;
    return pinnedAt + nav.getBoundingClientRect().height + GAP;
  }

  function bindNav() {
    document.addEventListener('click', function (e) {
      var chip = e.target.closest && e.target.closest('[data-brand-link]');
      if (!chip) return;
      var target = document.querySelector(chip.getAttribute('href'));
      if (!target) return;
      e.preventDefault();

      syncNavOffset();
      var top = target.getBoundingClientRect().top + window.scrollY - jumpOffset();
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });

    window.addEventListener('resize', syncNavOffset);
    window.addEventListener('load', syncNavOffset);
  }

  function init() {
    if (!document.querySelector(GROUPS_SEL)) return;
    readBrandMap();
    mergeFilterData();
    syncNavOffset();
    watchHeader();
    bindNav();
    bindPrice();
    connect();
    apply();
    applyPrice();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
